import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";

import PagedTable from "../shared/Tables/PagedTable";
import RegScanInput from "../shared/Inputs/RegScanInput";
import { ITriageQueueRow } from "../../models/Triage";
import { FinancialDecision, IVehicle } from "../../models/Vehicle";
import TableVehicleDetails from "../vehicles/VehiclesTable/TableVehicleDetails";
import useTriage from "../../data/useTriage";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useVehicleLookupMode from "../../hooks/useVehicleLookupMode";
import { identifierLabel, normaliseIdentifier } from "../../utils/vehicleIdentifier";
import { parseVehicleScan } from "../../utils/vehicleScan";
import useNotification from "../notifications/useNotifications";
import { useScannerOverride } from "../../contexts/ScannerContext";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";

const colHelper = createColumnHelper<ITriageQueueRow>();

export default function TriageTable() {
  const { t } = useTranslation("vehicles");
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { resolveExistingVehicle } = useVehiclesMutations();
  const { identifierType, isVinMode } = useVehicleLookupMode();
  const { createTriage } = useTriage();
  const isTme = CheckFeatureFlag("TME");
  // TME uses the Station Manager style reg input above the table; the same value
  // still filters the table via externalQuery.
  const [reg, setReg] = useState("");

  // Resolve a scanned id or a typed identifier to its triage (creating one if needed) and open
  // it. The triage page starts its timer on arrival once the vehicle has loaded.
  const handleLookupAndGo = async (target: { id?: string; identifier?: string }) => {
    const identifier = normaliseIdentifier(target.identifier, identifierType);
    if (!target.id && !identifier) {
      addNotification({
        variant: "warn",
        primaryText: t(isVinMode ? "Enter a VIN first" : "Enter a VRM first"),
      });
      return;
    }

    try {
      // Existing vehicles only — a typo'd reg must not conjure a new vehicle here.
      const vehicle = await resolveExistingVehicle.mutateAsync({ id: target.id, identifier });

      if (!vehicle?.id) {
        addNotification({
          variant: "warn",
          primaryText: t("No vehicle found for {{vrm}}", { vrm: identifier || target.id }),
          secondaryText: t(isVinMode ? "Check the VIN, or add the vehicle first." : "Check the reg, or add the vehicle first."),
        });
        return;
      }

      if (vehicle.triageId) {
        navigate(`/triage/${vehicle.triageId}`);
        return;
      }

      // TME buys before triaging, so don't create a triage for a vehicle that hasn't
      // been marked Purchased yet. An existing triage still opens (handled above).
      if (isTme && vehicle.financialDecision !== FinancialDecision.Purchased) {
        addNotification({
          variant: "warn",
          primaryText: t("Not yet purchased"),
        });
        return;
      }

      const triageResponse = await createTriage.mutateAsync({ vehicleId: vehicle.id });
      navigate(`/triage/${triageResponse.data.id}`);
    } catch (error) {
      addNotification({
        variant: "error",
        primaryText: t(isVinMode ? "Unable to lookup VIN and start triage" : "Unable to lookup VRM and start triage"),
      });
    }
  };

  // Scanning a vehicle tag ("v|{vehicleId}|{vrm}|{vin}") moves into its triage,
  // mirroring the Station Manager scan flow. The id on the tag is resolved first;
  // the printed plate or VIN is the fallback, reusing the same lookup as "Go".
  useScannerOverride((scan) => {
    const scanned = parseVehicleScan(scan.parts);
    if (!scanned) return;
    if (!scanned.id && !scanned.vrm && !scanned.vin) {
      addNotification({ variant: "warn", primaryText: t("Couldn't read the vehicle from that scan") });
      return;
    }
    handleLookupAndGo({ id: scanned.id, identifier: scanned.vrm || scanned.vin });
  });

  const columns = useMemo(
    () => [
      colHelper.accessor("vrm", {
        id: "Vehicle.Vrm",
        header: t("VRM"),
      }),
      colHelper.accessor("stockNumber", {
        id: "Vehicle.StockNumber",
        header: t("Stock #"),
        cell: (info) => info.getValue() ?? "-",
      }),
      colHelper.display({
        id: "vehicleDetails",
        header: t("Vehicle"),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const vehicle = {
            id: row.vehicleId,
            vrm: row.vrm ?? "",
            stockNumber: row.stockNumber ?? "",
            combinedMake: row.combinedMake ?? "",
            combinedModel: row.combinedModel ?? "",
            modelRangeDescription: row.modelRangeDescription ?? "",
            engineModelCode: row.engineModelCode ?? "",
            combinedVIN: "",
            transmission: "",
            fuelType: "",
            vehicleImageUrl: row.vehicleImageUrl ?? "",
          } as IVehicle;

          return <TableVehicleDetails vehicle={vehicle} />;
        },
      }),
      colHelper.accessor("engineModelCode", {
        id: "Vehicle.EngineModelCode",
        header: t("Engine Code"),
        cell: (info) => info.getValue() ?? "-",
      }),
      colHelper.display({
        id: "status",
        header: t("Triage"),
        enableSorting: false,
        cell: (info) => (
          <span>{info.row.original.completedSteps}/{info.row.original.totalSteps}</span>
        ),
      }),
    ],
    [t]
  );

  const isLookupLoading = resolveExistingVehicle.isLoading || createTriage.isLoading;

  return (
    <div className="flex flex-col gap-4">
      {isTme && (
        <div className="rounded-lg bg-white p-4 shadow">
          <RegScanInput
            value={reg}
            onChange={setReg}
            onSubmit={() => handleLookupAndGo({ identifier: reg })}
            buttonLabel={t("Go")}
            isLoading={isLookupLoading}
            identifierType={identifierType}
          />
        </div>
      )}

      <PagedTable<ITriageQueueRow>
        key="triage-queue"
        url="/triage/filtered"
        columns={columns}
        queryLabel={isTme ? undefined : t("Search VRM, stock, make, model")}
        externalQuery={isTme ? reg : undefined}
        queryButton={
          isTme
            ? undefined
            : {
                label: t("Go"),
                onClick: (query: string) => handleLookupAndGo({ identifier: query }),
                isLoading: isLookupLoading,
              }
        }
        showTotal={true}
        onRowClick={(row) => navigate(`/triage/${row.id}`)}
      />
    </div>
  );
}
